import { useState, useEffect, useRef } from 'react'
import { api } from '../context/AuthContext'
import { API_FILE_URL, GOOGLE_API_KEY } from '../constants/api'

const INP = { height: 40, borderRadius: 8, border: '1px solid #e9e9e4', padding: '0 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans',sans-serif" }
const LBL = { display: 'block', fontSize: 11, fontWeight: 700, color: '#111317', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5, fontFamily: "'JetBrains Mono',monospace" }

function Section({ title, icon, children, action }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #e9e9e4', background: '#fafaf8', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div style={{ fontSize: 14, fontWeight: 800, flex: 1 }}>{title}</div>
        {action}
      </div>
      <div style={{ padding: '20px 20px' }}>{children}</div>
    </div>
  )
}

// ── Google Places loader ──────────────────────────────────────────────────────
function loadGoogleMaps(apiKey) {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(); return }
    if (document.getElementById('gm-sp')) {
      const check = setInterval(() => { if (window.google?.maps?.places) { clearInterval(check); resolve() } }, 100)
      return
    }
    const s = document.createElement('script')
    s.id = 'gm-sp'
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
    s.async = true
    s.onload = resolve
    document.head.appendChild(s)
  })
}

// ── Address form modal ────────────────────────────────────────────────────────
const LABEL_OPTIONS = ['Shop', 'Warehouse', 'Pickup Point', 'Office', 'Other']

function AddressModal({ address, onClose, onSaved }) {
  const isEdit = !!address?.address_id
  const [form, setForm] = useState({
    label:             address?.label             || 'Shop',
    formatted_address: address?.formatted_address || '',
    city:              address?.city              || '',
    area:              address?.area              || '',
    notes:             address?.notes             || '',
    lat:               address?.lat               || null,
    lng:               address?.lng               || null,
  })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')
  const inputRef = useRef(null)
  const acRef    = useRef(null)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Attach Google Places Autocomplete to the address input
  useEffect(() => {
    let mounted = true
    loadGoogleMaps(GOOGLE_API_KEY).then(() => {
      if (!mounted || !inputRef.current) return
      acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: 'rw' },
        fields: ['formatted_address', 'geometry', 'address_components', 'name'],
      })
      acRef.current.addListener('place_changed', () => {
        const place = acRef.current.getPlace()
        if (!place.geometry) return
        const get = (types) => place.address_components?.find(a => types.some(t => a.types.includes(t)))?.long_name || ''
        setForm(f => ({
          ...f,
          formatted_address: place.formatted_address || place.name || '',
          city:  get(['locality', 'administrative_area_level_1']),
          area:  get(['sublocality_level_1', 'sublocality', 'neighborhood']),
          lat:   place.geometry.location.lat(),
          lng:   place.geometry.location.lng(),
        }))
      })
    })
    return () => { mounted = false }
  }, [])

  const handleSave = async () => {
    if (!form.formatted_address.trim()) { setError('Address is required.'); return }
    setSaving(true); setError('')
    try {
      const payload = {
        label:             form.label.trim() || 'Shop',
        formatted_address: form.formatted_address.trim(),
        city:              form.city  || null,
        area:              form.area  || null,
        notes:             form.notes || null,
        lat:               form.lat   || null,
        lng:               form.lng   || null,
      }
      const res = isEdit
        ? await api.put(`/address/${address.address_id}`, payload)
        : await api.post('/address', payload)
      onSaved(res.data.address)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save address.')
    } finally { setSaving(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 500 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 480, background: '#fff', borderRadius: 14, zIndex: 501, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{isEdit ? 'Edit Address' : '+ Add Address'}</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Label */}
          <div>
            <label style={LBL}>Label</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {LABEL_OPTIONS.map(opt => (
                <button key={opt} type="button" onClick={() => set('label', opt)}
                  style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${form.label === opt ? '#e8440a' : '#e9e9e4'}`, background: form.label === opt ? '#fff3f0' : '#fff', color: form.label === opt ? '#e8440a' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {opt}
                </button>
              ))}
              {!LABEL_OPTIONS.includes(form.label) && (
                <input value={form.label} onChange={e => set('label', e.target.value)} placeholder="Custom label"
                  style={{ ...INP, width: 120, height: 32, fontSize: 12 }} />
              )}
            </div>
          </div>

          {/* Address — Google Places autocomplete */}
          <div>
            <label style={LBL}>Address *</label>
            <input
              ref={inputRef}
              type="text"
              value={form.formatted_address}
              onChange={e => set('formatted_address', e.target.value)}
              placeholder="Search for address…"
              autoComplete="off"
              style={INP}
            />
          </div>

          {/* City + Area */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={LBL}>City</label>
              <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="e.g. Kigali" style={INP} />
            </div>
            <div>
              <label style={LBL}>Area / Sector</label>
              <input value={form.area} onChange={e => set('area', e.target.value)} placeholder="e.g. Remera" style={INP} />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={LBL}>Notes (optional)</label>
            <input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="e.g. Near Equity Bank, 2nd floor" style={INP} />
          </div>

          {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 13px', fontSize: 12, color: '#b91c1c' }}>⚠ {error}</div>}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 2, height: 42, borderRadius: 8, border: 'none', background: saving ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Address'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ShopSettings() {
  const [shop,        setShop]        = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [saving,      setSaving]      = useState(false)
  const [saved,       setSaved]       = useState(false)
  const [error,       setError]       = useState('')
  const [form,        setForm]        = useState({ shop_name: '', description: '', phone: '', location: '', tin: '' })
  const [bankForm,    setBankForm]    = useState({ bank_name: '', bank_holder: '', bank_account_no: '', bank_branch: '', payout_schedule: 'monthly' })
  const [bankSaving,  setBankSaving]  = useState(false)
  const [bankSaved,   setBankSaved]   = useState(false)
  const [avatarFile,  setAvatarFile]  = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(null)

  // Address state
  const [addresses,    setAddresses]    = useState([])
  const [addrLoading,  setAddrLoading]  = useState(false)
  const [addrModal,    setAddrModal]    = useState(null)
  const [deletingAddr, setDeletingAddr] = useState(null)
  const [settingDefault, setSettingDefault] = useState(null)

  // Document state
  const [documents,    setDocuments]    = useState([])
  const [docLoading,   setDocLoading]   = useState(false)
  const [uploading,    setUploading]    = useState(false)
  const [docType,      setDocType]      = useState('national_id')
  const [deletingDoc,  setDeletingDoc]  = useState(null)
  const docInputRef = useRef(null)

  // Delivery zone state
  const [allZones,        setAllZones]        = useState([])
  const [selectedZoneIds, setSelectedZoneIds] = useState([])
  const [fallbackFee,     setFallbackFee]     = useState('')
  const [zonesSaving,     setZonesSaving]     = useState(false)
  const [zonesSaved,      setZonesSaved]      = useState(false)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    api.get('/shops/my-shop')
      .then(res => {
        const s = res.data.shop || res.data
        setShop(s)
        setForm({
          shop_name:   s.shop_name     || '',
          description: s.description   || '',
          phone:       s.shop_phone    || s.phone || '',
          location:    s.shop_location || s.location || '',
          tin:         s.tin           || '',
        })
        if (s.shop_avatar) {
          const url = s.shop_avatar.startsWith('http') ? s.shop_avatar : `${API_FILE_URL}/${s.shop_avatar}`
          setAvatarPreview(url)
        }
        // Initialise bank account details
        setBankForm({
          bank_name:       s.bank_name       || '',
          bank_holder:     s.bank_holder     || '',
          bank_account_no: s.bank_account_no || '',
          bank_branch:     s.bank_branch     || '',
          payout_schedule: s.payout_schedule || 'monthly',
        })
        // Initialise delivery zone selection from saved shop data
        try {
          const ids = Array.isArray(s.delivery_zones) ? s.delivery_zones : JSON.parse(s.delivery_zones || '[]')
          setSelectedZoneIds(ids.map(Number))
        } catch { setSelectedZoneIds([]) }
        setFallbackFee(s.fallback_delivery_fee != null ? String(s.fallback_delivery_fee) : '')
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Load all available delivery zones (public endpoint)
  useEffect(() => {
    api.get('/settings/delivery-zones')
      .then(res => setAllZones(res.data.zones || []))
      .catch(() => setAllZones([]))
  }, [])

  // Load shop addresses
  useEffect(() => {
    setAddrLoading(true)
    api.get('/address')
      .then(res => setAddresses(res.data.addresses || []))
      .catch(() => setAddresses([]))
      .finally(() => setAddrLoading(false))
  }, [])

  const handleAvatar = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false)
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v) })
      if (avatarFile) fd.append('avatar', avatarFile)
      await api.put('/shops/update', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes.')
    } finally { setSaving(false) }
  }

  const handleAddrSaved = (saved) => {
    setAddresses(prev => {
      const exists = prev.find(a => a.address_id === saved.address_id)
      return exists ? prev.map(a => a.address_id === saved.address_id ? saved : a) : [...prev, saved]
    })
  }

  const handleSetDefault = async (addr) => {
    setSettingDefault(addr.address_id)
    try {
      await api.put(`/address/${addr.address_id}/default`)
      setAddresses(prev => prev.map(a => ({ ...a, is_default: a.address_id === addr.address_id })))
    } catch { alert('Failed to set default address') }
    finally { setSettingDefault(null) }
  }

  const handleDeleteAddr = async (addr) => {
    setDeletingAddr(addr.address_id)
    try {
      await api.delete(`/address/${addr.address_id}`)
      setAddresses(prev => prev.filter(a => a.address_id !== addr.address_id))
    } catch { alert('Failed to delete address') }
    finally { setDeletingAddr(null) }
  }

  // Load documents
  useEffect(() => {
    setDocLoading(true)
    api.get('/shops/my-documents')
      .then(res => setDocuments(res.data.documents || []))
      .catch(() => setDocuments([]))
      .finally(() => setDocLoading(false))
  }, [])

  const handleDocUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('document', file)
      fd.append('doc_type', docType)
      const res = await api.post('/shops/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setDocuments(prev => [res.data.document, ...prev])
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"?`)) return
    setDeletingDoc(doc.doc_id)
    try {
      await api.delete(`/shops/documents/${doc.doc_id}`)
      setDocuments(prev => prev.filter(d => d.doc_id !== doc.doc_id))
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete')
    } finally { setDeletingDoc(null) }
  }

  const handleSaveZones = async () => {
    setZonesSaving(true); setZonesSaved(false)
    try {
      const fd = new FormData()
      fd.append('name',                  form.shop_name || shop?.shop_name || '')
      fd.append('delivery_zones',        JSON.stringify(selectedZoneIds))
      fd.append('fallback_delivery_fee', fallbackFee !== '' ? fallbackFee : '')
      await api.put('/shops/update', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setZonesSaved(true)
      setTimeout(() => setZonesSaved(false), 3000)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save delivery zones')
    } finally { setZonesSaving(false) }
  }

  const toggleZone = (id) => setSelectedZoneIds(prev =>
    prev.includes(id) ? prev.filter(z => z !== id) : [...prev, id]
  )

  const setBank = (k, v) => setBankForm(f => ({ ...f, [k]: v }))

  const handleSaveBank = async () => {
    setBankSaving(true); setBankSaved(false)
    try {
      const fd = new FormData()
      fd.append('name',            form.shop_name || shop?.shop_name || '')
      fd.append('bank_name',       bankForm.bank_name)
      fd.append('bank_holder',     bankForm.bank_holder)
      fd.append('bank_account_no', bankForm.bank_account_no)
      fd.append('bank_branch',     bankForm.bank_branch)
      fd.append('payout_schedule', bankForm.payout_schedule)
      await api.put('/shops/update', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setBankSaved(true)
      setTimeout(() => setBankSaved(false), 3000)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save bank details')
    } finally { setBankSaving(false) }
  }

  if (loading) return <div style={{ padding: '60px 32px', textAlign: 'center', color: '#9a9a94', fontSize: 14 }}>Loading shop…</div>

  const avatarBg = (str = '') => { const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff']; let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h); return c[Math.abs(h) % c.length] }

  return (
    <div style={{ padding: '28px 32px 80px', fontFamily: "'Plus Jakarta Sans', sans-serif", maxWidth: 720 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Shop Settings</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Update your shop profile and business information.</div>
        </div>
        <button onClick={handleSave} disabled={saving}
          style={{ height: 40, padding: '0 22px', borderRadius: 9, border: 'none', background: saved ? '#15803d' : saving ? '#f0c4b4' : '#E8440A', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'background 0.2s' }}>
          {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>}

      {/* Avatar */}
      <Section icon="🖼" title="Shop Avatar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <label htmlFor="avatar-input" style={{ cursor: 'pointer' }}>
            <div style={{ width: 80, height: 80, borderRadius: 16, overflow: 'hidden', background: avatarBg(form.shop_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, border: '2px dashed #e9e9e4', position: 'relative' }}>
              {avatarPreview
                ? <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span>{(form.shop_name || 'S').charAt(0).toUpperCase()}</span>}
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                <span style={{ fontSize: 20, color: '#fff' }}>📷</span>
              </div>
            </div>
          </label>
          <input id="avatar-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatar} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{shop?.shop_name || 'Your Shop'}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>Click the avatar to change it.</div>
          </div>
        </div>
      </Section>

      {/* Basic info */}
      <Section icon="🏪" title="Basic Information">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={LBL}>Shop Name *</label>
            <input value={form.shop_name} onChange={e => set('shop_name', e.target.value)} placeholder="Your shop name" style={INP} />
          </div>
          <div>
            <label style={LBL}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell buyers what you sell…" rows={3}
              style={{ ...INP, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={LBL}>Shop Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+250 7XX XXX XXX" style={INP} />
            </div>
            <div>
              <label style={LBL}>TIN (Tax ID)</label>
              <input value={form.tin} onChange={e => set('tin', e.target.value)} placeholder="Tax identification number" style={INP} />
            </div>
          </div>
          <div>
            <label style={LBL}>Location</label>
            <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Kigali, Rwanda" style={INP} />
          </div>
        </div>
      </Section>

      {/* Shop Addresses */}
      <Section icon="📍" title="Shop Addresses"
        action={
          <button onClick={() => setAddrModal('add')}
            style={{ height: 32, padding: '0 14px', borderRadius: 7, border: 'none', background: '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            + Add Address
          </button>
        }>

        {addrLoading ? (
          <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '16px 0' }}>Loading addresses…</div>
        ) : addresses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📍</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>No addresses yet</div>
            <div style={{ fontSize: 12, color: '#9a9a94', marginBottom: 14 }}>Add your shop location so buyers can find you.</div>
            <button onClick={() => setAddrModal('add')}
              style={{ height: 34, padding: '0 16px', borderRadius: 7, border: 'none', background: '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              + Add First Address
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {addresses.map(addr => (
              <div key={addr.address_id}
                style={{ background: addr.is_default ? '#f0fdf4' : '#f9f9f7', border: `1px solid ${addr.is_default ? '#bbf7d0' : '#e9e9e4'}`, borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>

                {/* Icon + default badge */}
                <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>📍</div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{addr.label || 'Shop'}</span>
                    {addr.is_default && (
                      <span style={{ fontSize: 9, fontWeight: 700, background: '#bbf7d0', color: '#15803d', padding: '1px 6px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>Default</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{addr.formatted_address}</div>
                  {(addr.city || addr.area) && (
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
                      {[addr.area, addr.city].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {addr.notes && (
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, fontStyle: 'italic' }}>{addr.notes}</div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {!addr.is_default && (
                    <button onClick={() => handleSetDefault(addr)} disabled={settingDefault === addr.address_id}
                      style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: 'nowrap' }}>
                      {settingDefault === addr.address_id ? '…' : 'Set Default'}
                    </button>
                  )}
                  <button onClick={() => setAddrModal(addr)}
                    style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', color: '#374151', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    Edit
                  </button>
                  <button onClick={() => handleDeleteAddr(addr)} disabled={deletingAddr === addr.address_id}
                    style={{ height: 28, padding: '0 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    {deletingAddr === addr.address_id ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Shop Stats */}
      <Section icon="📊" title="Shop Stats">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {[
            ['Products',  Number(shop?.product_count  ?? 0).toLocaleString()],
            ['Orders',    Number(shop?.order_count    ?? 0).toLocaleString()],
            ['Revenue',   `RWF ${Number(shop?.revenue ?? 0).toLocaleString()}`],
            ['Followers', Number(shop?.follower_count ?? 0).toLocaleString()],
            ['Status',    shop?.status === 1 ? '✅ Active' : shop?.status === 2 ? '⏳ Pending' : '🚫 Suspended'],
            ['Joined',    shop?.created_at ? new Date(shop.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ background: '#f9f9f7', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 4 }}>{k}</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Delivery Zones */}
      <Section icon="🚚" title="Delivery Zones"
        action={
          <button onClick={handleSaveZones} disabled={zonesSaving}
            style={{ height: 32, padding: '0 16px', borderRadius: 7, border: 'none', background: zonesSaved ? '#15803d' : zonesSaving ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: zonesSaving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'background 0.2s' }}>
            {zonesSaved ? '✓ Saved!' : zonesSaving ? 'Saving…' : 'Save Zones'}
          </button>
        }>

        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
          Select the delivery zones you cover. Buyers in these zones will see your delivery fee when ordering.
        </div>

        {allZones.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '20px 0', background: '#f9f9f7', borderRadius: 8, border: '1px dashed #e9e9e4' }}>
            No delivery zones configured yet. Ask your admin to add zones in Settings.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {allZones.map(zone => {
              const selected = selectedZoneIds.includes(Number(zone.zone_id))
              return (
                <div key={zone.zone_id}
                  onClick={() => toggleZone(Number(zone.zone_id))}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: selected ? '#fff3f0' : '#f9f9f7', border: `1.5px solid ${selected ? '#fdd0c0' : '#e9e9e4'}`, borderRadius: 10, cursor: 'pointer', transition: 'all 0.12s' }}>
                  {/* Checkbox */}
                  <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${selected ? '#e8440a' : '#d1d5db'}`, background: selected ? '#e8440a' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.12s' }}>
                    {selected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                  </div>
                  {/* Zone info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: selected ? '#e8440a' : '#111317' }}>{zone.zone_name}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>
                      {[zone.area, zone.city, zone.district].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {/* Fee */}
                  <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: selected ? '#e8440a' : '#374151', flexShrink: 0 }}>
                    RWF {Number(zone.fee || 0).toLocaleString()}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Fallback fee */}
        <div>
          <label style={LBL}>Fallback Delivery Fee (RWF)</label>
          <input
            type="number" min="0"
            value={fallbackFee}
            onChange={e => setFallbackFee(e.target.value)}
            placeholder="e.g. 2000 — used when buyer address is outside all zones"
            style={INP}
          />
          <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 5 }}>
            Applied when the buyer's address doesn't match any of your selected zones.
          </div>
        </div>
      </Section>

      {/* Bank Account */}
      <Section icon="🏦" title="Bank Account & Payout"
        action={
          <button onClick={handleSaveBank} disabled={bankSaving}
            style={{ height: 32, padding: '0 16px', borderRadius: 7, border: 'none', background: bankSaved ? '#15803d' : bankSaving ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: bankSaving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'background 0.2s' }}>
            {bankSaved ? '✓ Saved!' : bankSaving ? 'Saving…' : 'Save Bank Details'}
          </button>
        }>

        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
          Your bank details are used by the Kudos accountant to process your payout transfers.
          Make sure the account name matches exactly what's on your bank account.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={LBL}>Bank Name</label>
              <select value={bankForm.bank_name} onChange={e => setBank('bank_name', e.target.value)} style={{ ...INP, cursor: 'pointer' }}>
                <option value="">Select bank…</option>
                {['Bank of Kigali', 'Equity Bank', 'I&M Bank', 'Cogebanque', 'Ecobank', 'GT Bank', 'Access Bank', 'BPR Atlas Mara', 'Urwego Bank', 'KCB Bank', 'NCBA', 'Zigama CSS', 'Other'].map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={LBL}>Branch (optional)</label>
              <input value={bankForm.bank_branch} onChange={e => setBank('bank_branch', e.target.value)}
                placeholder="e.g. Kigali City Tower" style={INP} />
            </div>
          </div>

          <div>
            <label style={LBL}>Account Holder Name *</label>
            <input value={bankForm.bank_holder} onChange={e => setBank('bank_holder', e.target.value)}
              placeholder="Full name exactly as on your bank account" style={INP} />
            <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4 }}>
              Must match the name registered with the bank — used for bank transfer verification.
            </div>
          </div>

          <div>
            <label style={LBL}>Account Number *</label>
            <input value={bankForm.bank_account_no} onChange={e => setBank('bank_account_no', e.target.value)}
              placeholder="e.g. 00012345678" style={INP} />
          </div>

          <div>
            <label style={LBL}>Payout Schedule</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { val: 'monthly',   label: '📅 Monthly',    desc: 'Paid at end of each month' },
                { val: 'on_demand', label: '⚡ On Demand',   desc: 'Request payout anytime'   },
              ].map(opt => (
                <div key={opt.val} onClick={() => setBank('payout_schedule', opt.val)}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${bankForm.payout_schedule === opt.val ? '#e8440a' : '#e9e9e4'}`, background: bankForm.payout_schedule === opt.val ? '#fff3f0' : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.12s' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: bankForm.payout_schedule === opt.val ? '#e8440a' : '#374151' }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>{opt.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {!bankForm.bank_account_no && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>⚠</span>
              <span>No bank account added yet. You won't appear on the payout list until you add one.</span>
            </div>
          )}
        </div>
      </Section>

      {/* Verification Documents */}
      <Section icon="📄" title="Verification Documents"
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select value={docType} onChange={e => setDocType(e.target.value)}
              style={{ height: 32, border: '1px solid #e9e9e4', borderRadius: 7, padding: '0 10px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif", cursor: 'pointer' }}>
              <option value="national_id">National ID / Passport</option>
              <option value="business_registration">Business Registration</option>
              <option value="tin_certificate">TIN Certificate</option>
              <option value="bank_statement">Bank Statement</option>
              <option value="other">Other</option>
            </select>
            <input type="file" ref={docInputRef} onChange={handleDocUpload} accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} />
            <button onClick={() => docInputRef.current?.click()} disabled={uploading}
              style={{ height: 32, padding: '0 14px', borderRadius: 7, border: 'none', background: uploading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              {uploading ? '⏳ Uploading…' : '⬆ Upload'}
            </button>
          </div>
        }>

        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
          Upload documents for account verification. Accepted: PDF, JPG, PNG (max 10 MB each).
          Admin will review and approve each document.
        </div>

        {docLoading ? (
          <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '24px 0' }}>Loading documents…</div>
        ) : documents.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '24px 0', background: '#f9f9f7', borderRadius: 8, border: '1px dashed #e9e9e4' }}>
            No documents uploaded yet. Select a type and click Upload.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {documents.map(doc => {
              const STATUS = {
                pending:  { color: '#b45309', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b', label: 'Pending Review' },
                approved: { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', dot: '#16a34a', label: 'Approved'       },
                rejected: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444', label: 'Rejected'       },
              }
              const s = STATUS[doc.status] || STATUS.pending
              const DOC_LABEL = {
                national_id: 'National ID / Passport', business_registration: 'Business Registration',
                tin_certificate: 'TIN Certificate', bank_statement: 'Bank Statement', other: 'Other',
              }
              return (
                <div key={doc.doc_id} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  {/* File icon */}
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.7)', border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {doc.file_url?.toLowerCase().endsWith('.pdf') ? '📋' : '🖼'}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#111317', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{doc.file_name}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.7)', border: `1px solid ${s.border}`, borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700, color: s.color, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />
                        {s.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace", marginBottom: 2 }}>
                      {DOC_LABEL[doc.doc_type] || doc.doc_type}
                      {doc.created_at && (
                        <span style={{ color: '#9a9a94' }}> · {new Date(doc.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      )}
                    </div>
                    {doc.status === 'rejected' && doc.admin_note && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#b91c1c', background: 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '4px 8px', border: '1px solid #fecaca', lineHeight: 1.5 }}>
                        ⚠ Admin note: {doc.admin_note}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <a href={doc.file_url} target="_blank" rel="noreferrer"
                      style={{ height: 28, padding: '0 10px', borderRadius: 6, border: `1px solid ${s.border}`, background: 'rgba(255,255,255,0.7)', color: '#374151', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      View ↗
                    </a>
                    {doc.status !== 'approved' && (
                      <button onClick={() => handleDeleteDoc(doc)} disabled={deletingDoc === doc.doc_id}
                        style={{ height: 28, padding: '0 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 10, fontWeight: 700, cursor: deletingDoc === doc.doc_id ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: 'nowrap' }}>
                        {deletingDoc === doc.doc_id ? '…' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>

      {/* Address modal */}
      {addrModal && (
        <AddressModal
          address={addrModal === 'add' ? null : addrModal}
          onClose={() => setAddrModal(null)}
          onSaved={(saved) => { handleAddrSaved(saved); setAddrModal(null) }}
        />
      )}
    </div>
  )
}
